import { operatorStateLabel } from '@/features/condition-builder';
import { isRecord } from '@/utils/objects';
import type { AttributeNode, AttributeSource, ParseOptions, TriggerRelationshipNode } from '../types';
import { firstAndFilter, readFilterFields, toString } from './parse-helpers';

// Attribute-like types from backend: object_attribute, event_attribute, etc.
// These all share the same { field, operator, value } shape.
const ATTRIBUTE_LIKE_TYPES = new Set([
  'attribute',
  'object_attribute',
  'event_attribute',
  'foreign_event_attribute',
  'inbound_attribute',
  'collection_attribute',
  'relationship_attribute',
  'journey_attribute',
]);

/**
 * The flat attribute-like types whose field is not read off the person, and
 * the attribute source each one names.
 */
const FLAT_ATTRIBUTE_SOURCES: ReadonlyMap<string, AttributeSource> = new Map([
  ['relationship_attribute', 'relationship'],
  ['event_attribute', 'event'],
  ['journey_attribute', 'journey'],
  ['object_attribute', 'object'],
]);

/**
 * The `field_source` values that mark a flat attribute as read from somewhere other than its type
 * says: a plain attribute sourced from the journey, as Ember's `deserializeFilter` reads it, and a
 * workflow `related` condition's untyped refinement row, which Ember's
 * `deserializeRelatedWorkflowRelationshipCondition` reads as an object or relationship attribute.
 */
const FLAT_FIELD_SOURCES: ReadonlyMap<unknown, AttributeSource> = new Map([
  ['journey_attribute', 'journey'],
  ['relationship_attribute', 'relationship'],
  ['object_attribute', 'object'],
]);

/** The stored source that names the trigger event's data. */
const TRIGGER_EVENT_ATTRIBUTE = 'event_attribute';

/**
 * The source a stored trigger event attribute is read from: the trigger
 * event, or on a webhook-triggered campaign the webhook's data.
 */
function triggerSource(source: AttributeSource, options: ParseOptions): AttributeSource {
  return source === 'event' && options.isWebhookTriggered === true ? 'webhook' : source;
}

/**
 * The leaf keys a campaign trigger tree nests its filter block under, and the
 * attribute source each one names.
 *
 * A trigger tree spells its leaves differently from the flat filter shapes
 * above: the block sits under a key naming what the field is read from, rather
 * than the fields sitting on the leaf itself. `attribute` reads the person, so
 * it carries no source; the other two belong to a relationship trigger and
 * mark which side of the relationship the field comes from.
 */
const NESTED_ATTRIBUTE_KEYS: ReadonlyArray<{ key: string; source?: AttributeSource }> = [
  { key: 'attribute' },
  { key: 'object_attribute', source: 'object' },
  { key: 'relationship_attribute', source: 'relationship' },
];

/**
 * The keys a backend filter block sits under, all carrying the same
 * `{ field, operator, value }` shape and differing only in what they are
 * about: the person's attribute, a related object's, or a relationship's.
 */
const FILTER_BLOCK_KEYS = ['attribute_filter', 'related_object_attr_filter', 'relationship_attr_filter'];

/** The first backend filter block on the node, read as an attribute. */
function parseFilterBlock(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  for (const key of FILTER_BLOCK_KEYS) {
    const block = raw[key];
    if (!isRecord(block)) {
      continue;
    }
    const field = toString(block.attribute ?? block.field);
    return field == null ? null : { type: 'attribute', field, ...readFilterFields(block) };
  }
  return null;
}

/**
 * A saved filter referenced by id, as a placeholder row.
 *
 * The filter itself lives elsewhere and cannot be fetched from inside a
 * parser, so the row names the reference rather than what it holds.
 */
function parseSavedFilter(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  const conditionId = raw.condition_id;
  if (typeof conditionId !== 'number' || conditionId <= 0) {
    return null;
  }
  if ('field' in raw || 'attribute' in raw || 'event' in raw || 'segment' in raw) {
    return null;
  }
  const inverse = raw.inverse === true;
  return {
    type: 'attribute',
    field: `saved filter #${conditionId}`,
    operator: inverse ? 'does not match' : 'matches',
    inverse,
  };
}

/** The operator label an email-validation condition reads as. */
function emailOperatorLabel(operator: string | undefined, inverse: boolean): string {
  if (operator === 'email_valid') {
    return inverse ? 'is not a valid email' : 'is a valid email';
  }
  if (operator === 'exists') {
    return inverse ? 'does not exist' : 'exists';
  }
  return inverse ? 'is not valid or does not exist' : 'is valid and exists';
}

/** An email-validation condition: `{ type: 'email', operator, inverse }`. */
function parseEmailValidation(raw: unknown): AttributeNode | null {
  if (!isRecord(raw) || raw.type !== 'email') {
    return null;
  }
  const inverse = raw.inverse === true;
  return {
    type: 'attribute',
    field: 'email',
    operator: emailOperatorLabel(toString(raw.operator), inverse),
    inverse,
  };
}

/** The newsletter a newsletter-recipient condition is about: its name, else its id, else a plain noun. */
function newsletterNameOf(raw: unknown): string {
  if (!isRecord(raw)) {
    return 'newsletter';
  }
  const block = isRecord(raw.newsletter_recipients) ? raw.newsletter_recipients : undefined;
  const newsletterName = toString(raw.newsletter_name ?? block?.name);
  if (newsletterName != null) {
    return newsletterName;
  }
  const newsletterId = toString(block?.id ?? raw.newsletter_id);
  return newsletterId != null ? `newsletter #${newsletterId}` : 'newsletter';
}

/** A newsletter-recipient condition, named by the newsletter it is about. */
function parseNewsletterRecipients(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (raw.type !== 'newsletter_recipients' && !('newsletter_recipients' in raw)) {
    return null;
  }
  const inverse = raw.inverse === true || raw.negate === true;
  const name = newsletterNameOf(raw);
  return {
    type: 'attribute',
    field: `${inverse ? 'is not a recipient of' : 'is a recipient of'} ${name}`,
    inverse,
    source: 'newsletter',
  };
}

/**
 * An attribute leaf whose filter block is nested under a key naming its
 * source, or `null` when the node carries no such block.
 *
 * Takes the node as `unknown` and narrows it here, so the one dictionary
 * contract in this module stays on the parser's own entry point.
 */
function parseNestedAttribute(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  for (const { key, source } of NESTED_ATTRIBUTE_KEYS) {
    const block = raw[key];
    if (!isRecord(block)) {
      continue;
    }
    const field = toString(block.field ?? block.attribute);
    if (field == null) {
      continue;
    }
    const node: AttributeNode = { type: 'attribute', field, ...readFilterFields(block) };
    if (source != null) {
      node.source = source;
    }
    return node;
  }
  return null;
}

/**
 * Which attribute a workflow step's comparison reads its field from, by
 * `field_source`. A person attribute carries no source.
 */
const COMPARE_FIELD_SOURCES: ReadonlyMap<string, AttributeSource | null> = new Map([
  ['customer_attribute', null],
  ['event_attribute', 'event'],
  ['foreign_event_attribute', 'event'],
  ['journey_attribute', 'journey'],
  ['trigger_object_attribute', 'object'],
  // Ember reads this as a relationship attribute when it has no campaign to name
  // the trigger by (ui `filters-display-tree.ts` keepsLegacyCompareLabel).
  ['trigger_relationship_attribute', 'relationship'],
]);

/** The `field_source` of a comparison on the trigger relationship's own attribute. */
const TRIGGER_RELATIONSHIP_ATTRIBUTE = 'trigger_relationship_attribute';

/** What a comparison's value names, by `value_source`. A literal has no label. */
const COMPARE_VALUE_SOURCE_LABELS: ReadonlyMap<string, string | null> = new Map([
  ['const', null],
  ['', null],
  ['customer_attribute', "the current person's attribute"],
  ['event_attribute', 'the trigger event attribute'],
  ['journey_attribute', 'the journey attribute'],
  // An object or relationship campaign's comparisons (Ember `ObjectRelationshipCampaignComparisonOperator`).
  ['relationship_attribute', "the current person's relationship attribute"],
  ['trigger_object_attribute', 'the trigger object attribute'],
  ['trigger_relationship_attribute', "the person added to the object's relationship attribute"],
  ['trigger_person_attribute', "the person added to the object's attribute"],
]);

/** What a comparison's value names; on a webhook-triggered campaign the trigger event attribute is the webhook's. */
function compareValueSource(valueSource: string, options: ParseOptions): string | null | undefined {
  if (valueSource === TRIGGER_EVENT_ATTRIBUTE && options.isWebhookTriggered === true) {
    return 'the trigger attribute';
  }
  return COMPARE_VALUE_SOURCE_LABELS.get(valueSource);
}

/** The `field_source` of a comparison that reads a journey attribute. */
const JOURNEY_FIELD_SOURCE = 'journey_attribute';

/**
 * A journey attribute comparison's operator in the attribute editor's words, "is equal to".
 *
 * Ember keeps a journey compare on its legacy row label rather than the segment builder's sentence
 * (`keepsLegacyCompareLabel` in `app/utils/campaigns/filters-display-tree.ts`), and that label words
 * the operator from the editor's operator table (`app/utils/operators.ts`).
 */
function journeyCompareOperator(operator: string | undefined, inverse: boolean): string | undefined {
  if (operator == null) {
    return undefined;
  }
  return operatorStateLabel(inverse && !operator.startsWith('!') ? `!${operator}` : operator);
}

/**
 * The flat comparison leaf a workflow step's conditions store:
 * `{ type: 'attribute_compare', field_source, field, operator, value, value_source }`.
 * Negation is a `!` on the operator, or an `inverse` flag. A field or value source this viewer has
 * no words for is left to the unknown row.
 */
export function parseAttributeCompare(raw: unknown, options: ParseOptions = {}): AttributeNode | null {
  if (!isRecord(raw) || raw.type !== 'attribute_compare') {
    return null;
  }
  const field = toString(raw.field);
  const fieldSource = toString(raw.field_source) ?? '';
  const source = COMPARE_FIELD_SOURCES.get(fieldSource);
  const valueSource = compareValueSource(toString(raw.value_source) ?? '', options);
  if (field == null || source === undefined || valueSource === undefined) {
    return null;
  }
  const node: AttributeNode = { type: 'attribute', field, ...readFilterFields(raw) };
  // Negated by a `!` on the operator, or by an `inverse` flag beside a plain one.
  node.inverse = node.inverse || toString(raw.operator)?.startsWith('!') === true;
  if (fieldSource === JOURNEY_FIELD_SOURCE) {
    node.operator = journeyCompareOperator(toString(raw.operator), node.inverse) ?? node.operator;
  }
  if (source != null) {
    node.source = fieldSource === TRIGGER_EVENT_ATTRIBUTE ? triggerSource(source, options) : source;
  }
  if (valueSource != null) {
    node.valueSource = valueSource;
  }
  return node;
}

/**
 * A comparison on the trigger relationship's attribute, which Ember words by
 * the person the relationship joins (`cio-condition-label-item/relationship`):
 * `The person added to the Account where their relationship role is equal to
 * admin`. Its operator reads as a state, `is equal to`, as that label's
 * attribute viewer words it. Without a trigger object type the caller has no
 * campaign to name it by, so it reads as a relationship attribute instead.
 */
function parseTriggerRelationshipCompare(raw: unknown, options: ParseOptions): TriggerRelationshipNode | null {
  if (!isRecord(raw) || raw.field_source !== TRIGGER_RELATIONSHIP_ATTRIBUTE || options.triggerObjectTypeId == null) {
    return null;
  }
  const field = toString(raw.field);
  // A value source this viewer has no words for is left to the unknown row, as `parseAttributeCompare` does.
  const valueSource = compareValueSource(toString(raw.value_source) ?? '', options);
  if (field == null || valueSource === undefined) {
    return null;
  }
  const node: TriggerRelationshipNode = {
    type: 'trigger_relationship',
    objectTypeId: options.triggerObjectTypeId,
    isRelationshipChange: options.isRelationshipChangeTrigger === true,
    field,
    ...triggerRelationshipComparison(readFilterFields(raw), toString(raw.operator)),
  };
  if (valueSource != null) {
    node.valueSource = valueSource;
  }
  return node;
}

/** A trigger relationship comparison's operator, read as a state (`is equal to`), its values and its negation. */
function triggerRelationshipComparison(
  fields: ReturnType<typeof readFilterFields>,
  storedOperator: string | undefined,
): Pick<TriggerRelationshipNode, 'operator' | 'value' | 'value2' | 'inverse'> {
  const { operator: shortOperator, value, value2, inverse } = fields;
  const operator = negatedOperator(storedOperator, inverse);
  return {
    operator: operatorStateLabel(operator) ?? shortOperator,
    value,
    value2,
    inverse: inverse || operator?.startsWith('!') === true,
  };
}

/** The stored operator with its `inverse` flag folded into a `!` prefix, as the operator vocabulary spells a negation. */
function negatedOperator(stored: string | undefined, inverse: boolean): string | undefined {
  if (stored == null || !inverse || stored.startsWith('!')) {
    return stored;
  }
  return `!${stored}`;
}

/**
 * Whether the node is a flat attribute leaf: `{ type: 'attribute', field,
 * operator, value }`, one of its attribute-like siblings, the backend's type 0,
 * or a bare `{ field, operator, value }`.
 */
function isFlatAttribute(raw: unknown): boolean {
  if (!isRecord(raw)) {
    return false;
  }
  const condType = raw.type;
  const hasField = 'field' in raw || 'attribute' in raw;
  return (
    (typeof condType === 'string' && ATTRIBUTE_LIKE_TYPES.has(condType)) ||
    condType === 0 ||
    condType === '0' ||
    (hasField && condType == null)
  );
}

/** A flat attribute leaf, marked with its source where its type names one. */
function parseFlatAttribute(raw: unknown, options: ParseOptions): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  const field = toString(raw.field ?? raw.attribute ?? raw.name);
  if (field == null) {
    return null;
  }
  const node: AttributeNode = { type: 'attribute', field, ...readFilterFields(raw) };
  const typeSource = typeof raw.type === 'string' ? FLAT_ATTRIBUTE_SOURCES.get(raw.type) : undefined;
  const source = FLAT_FIELD_SOURCES.get(raw.field_source) ?? typeSource;
  if (source != null) {
    node.source = triggerSource(source, options);
  }
  return node;
}

/**
 * Parse attribute conditions.
 * Handles: { attribute: { field, operator, value } } and its object and
 *            relationship siblings, as a trigger tree nests them
 *          { event: { type: 'attribute_change', ... } }
 *          { attribute_filter: { field, operator, value } }  (backend type: 0)
 *          { type: 'attribute', field, operator, value }
 *          { field, operator, value }  (bare filter)
 */
export function parseAttribute(
  raw: Record<string, unknown>,
  options: ParseOptions = {},
): AttributeNode | TriggerRelationshipNode | null {
  const nested = parseNestedAttribute(raw);
  if (nested != null) {
    return nested;
  }

  // Ember format: { event: { type: 'attribute_change', name, filters: { and: [to, from] } } }
  const event = isRecord(raw.event) ? raw.event : undefined;
  if (event?.type === 'attribute_change') {
    const name = toString(event.name);
    if (name == null) return null;
    return {
      type: 'attribute',
      field: name,
      ...readFilterFields(firstAndFilter(event)),
    };
  }

  if (FILTER_BLOCK_KEYS.some((key) => isRecord(raw[key]))) {
    return parseFilterBlock(raw);
  }

  if (raw.type === 'attribute_compare') {
    return parseTriggerRelationshipCompare(raw, options) ?? parseAttributeCompare(raw, options);
  }

  if (isFlatAttribute(raw)) {
    return parseFlatAttribute(raw, options);
  }

  return parseSavedFilter(raw) ?? parseEmailValidation(raw) ?? parseNewsletterRecipients(raw);
}
